import React from 'react';
import { Head, Link } from '@inertiajs/react';
import { Card } from '../../components/ui/card';

export type InviteErrorType = 'not_found' | 'expired' | 'used';

interface InviteErrorProps {
  error: InviteErrorType;
  inviteName?: string;
}

const errorMessages: Record<InviteErrorType, { title: string; message: string }> = {
  not_found: {
    title: 'Invalid Invite Link',
    message: 'This invite link does not exist.',
  },
  expired: {
    title: 'Invite Link Expired',
    message: 'This invite link has expired.',
  },
  used: {
    title: 'Invite Link Already Used',
    message: 'This invite link has already been used.',
  },
};

const InviteError: React.FC<InviteErrorProps> = ({ error, inviteName }) => {
  const errorInfo = errorMessages[error] || errorMessages.not_found;

  return (
    <div className="min-h-screen w-full bg-background">
      <Head title={errorInfo.title} />

      <div className="container mx-auto px-4 py-8 md:py-16 max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {errorInfo.title}
          </h1>
          {inviteName && (
            <p className="text-muted-foreground">
              Invitation for {inviteName}
            </p>
          )}
        </div>

        {/* Error Card */}
        <Card className="p-8 md:p-12">
          <div className="flex flex-col items-center text-center space-y-6">
            {/* Error Icon */}
            <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-10 h-10 text-destructive"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            {/* Error Message */}
            <div className="space-y-2">
              <p className="text-lg text-foreground">
                {errorInfo.message}
              </p>
              {error === 'not_found' && (
                <p className="text-sm text-muted-foreground">
                  Please check the link and try again, or contact the person who sent you this invite.
                </p>
              )}
              {error === 'expired' && (
                <p className="text-sm text-muted-foreground">
                  This invite link has reached its expiration date. Please request a new invite.
                </p>
              )}
              {error === 'used' && (
                <p className="text-sm text-muted-foreground">
                  This invite link has already been used.
                </p>
              )}
            </div>

            {/* Home Link */}
            <div className="pt-4">
              <Link
                href="/"
                className="text-primary hover:text-primary/80 font-medium"
              >
                Return to Home
              </Link>
            </div>
          </div>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-muted-foreground">
          <p>If you believe this is an error, please contact the invite sender.</p>
        </div>
      </div>
    </div>
  );
};

export default InviteError;
